import { getSingleValueForTasField, setSingleValueForTasField, type FieldName } from './fields.ts';
import { buildSchoolFilter } from './build-school-filter.ts';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TASCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';

/**
 * Saves the courses begin/end date onto the application based on the program template's
 * lock status. Returns true if the date was newly applied, false if the locked field
 * already had a value.
 */
export function saveCoursesDate(
  date: string,
  application: TasApplicationModel,
  lockStatus: string
): boolean {
  const field: FieldName =
    lockStatus === 'COURSE_END_DATE' ? 'COURSES_END_DATE' : 'COURSES_BEGIN_DATE';

  const fieldValue = getSingleValueForTasField(field, application.fields);
  if (fieldValue) {
    return false;
  }
  setSingleValueForTasField(field, date, application.fields);
  return true;
}

/**
 * Looks up the previously selected school for a TAS program instance, if any is stored.
 * Returns undefined when no institution IDs are present or when the lookup fails.
 */
export async function loadSelectedSchool(
  programInstance: TasProgramInstanceModel,
  store: Store
): Promise<TasUnifiedInstitutionModel | undefined> {
  try {
    const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
    const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

    if (!tasInstitutionId && !dapipInstitutionId) {
      return undefined;
    }

    const queryOptions: Record<string, string | number> = {
      'page[limit]': 1,
      'page[offset]': 0,
    };
    for (const [key, value] of Object.entries(buildSchoolFilter(programInstance))) {
      if (value !== undefined) {
        queryOptions[`filter[${key}]`] = value;
      }
    }
    const { content } = (await store.request(
      query<TasUnifiedInstitutionModel>('tas-unified-institution', queryOptions)
    )) as { content: { data: TasUnifiedInstitutionModel[] } };
    return content.data[0];
  } catch (e) {
    console.error('Error attempting to load previously selected school', e);
    return undefined;
  }
}

/**
 * Rolls back any unsaved tas-course records in the store. Call from a route's
 * `deactivate()` hook so the cleanup runs when the user leaves the route.
 */
export function rollbackUnsavedCourses(store: Store): void {
  const courses = store.peekAll('tas-course') as TASCourseModel[];
  courses.filter((course) => course.isNew).forEach((course) => course.rollbackAttributes());
}
